import { Alert, Button, Card, Form, Input, Typography } from 'antd';
import React, { useRef, useState } from 'react';
import { Navigate } from 'react-router-dom';
import styled from 'styled-components';
import useAuth from '../../auth/useAuth';

const { Title, Text } = Typography;

const Page = styled.div`
  min-height: 100vh;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 24px;
`;

const StyledCard = styled(Card)`
  width: 100%;
  max-width: 380px;
`;

const Header = styled.div`
  text-align: center;
  margin-bottom: 24px;
`;

function Login() {
  const { signIn, isAuthenticated } = useAuth();
  const [error, setError] = useState(null);
  const [submitting, setSubmitting] = useState(false);

  // Only bounce people who were *already* signed in when they landed here.
  // Reading this at mount keeps the redirect from firing mid-sign-in, which
  // would override the path signIn is navigating back to.
  const wasSignedInOnArrival = useRef(isAuthenticated);

  const onFinish = async ({ email, password }) => {
    setSubmitting(true);
    setError(null);
    try {
      await signIn(email, password);
      // signIn navigates on success; nothing else to do here.
    } catch (err) {
      setError(err.message);
      setSubmitting(false);
    }
  };

  if (wasSignedInOnArrival.current) {
    return <Navigate to="/" replace />;
  }

  return (
    <Page>
      <StyledCard>
        <Header>
          <Title level={3} style={{ marginBottom: 4 }}>
            Sign in
          </Title>
          <Text type="secondary">Fathom text analysis</Text>
        </Header>

        {error && <Alert type="error" message={error} showIcon style={{ marginBottom: 16 }} />}

        <Form layout="vertical" onFinish={onFinish} requiredMark={false} disabled={submitting}>
          <Form.Item
            name="email"
            label="Email"
            rules={[{ required: true, message: 'Please enter your email.' }]}
          >
            <Input
              type="email"
              autoComplete="username"
              placeholder="you@example.com"
              size="large"
              data-cy="login-email"
              autoFocus
            />
          </Form.Item>

          <Form.Item
            name="password"
            label="Password"
            rules={[{ required: true, message: 'Please enter your password.' }]}
          >
            <Input.Password autoComplete="current-password" size="large" data-cy="login-password" />
          </Form.Item>

          <Button
            type="primary"
            htmlType="submit"
            size="large"
            block
            loading={submitting}
            data-cy="login-submit"
          >
            Sign in
          </Button>
        </Form>
      </StyledCard>
    </Page>
  );
}

export default Login;
